import { create } from 'zustand';

/**
 * @fileoverview The regime's currency, app-wide, for formatters that cannot
 * call a hook.
 *
 * A UK CBAM declarant's money is sterling; an EU CBAM declarant's is euros —
 * and apart from the glyph the two read identically. `formatCurrency`
 * (`helpers/numberFormat.js`) is a plain function called from React components,
 * from Excel/PDF export generators and from data-prep utilities, so it cannot
 * ask a React Query hook what regime it is in. It reads this store instead,
 * exactly as it already reads the user's decimal convention from
 * `useDecimalConventionStore` — the same problem, the same shape.
 *
 * ── Why this is safe for EU ────────────────────────────────────────────────
 *
 * The store holds a currency ONLY when the server has said what it is. Until
 * then `currency` is `null` and `getRegimeCurrency()` answers `'EUR'`, which
 * is the value `formatCurrency`'s default has always had. So an EU declarant,
 * a signed-out visitor on the public catalog, an operator, a consultant and a
 * lite supplier all render precisely what they rendered before — in every
 * state: before the profile resolves, if the request fails, and if nothing
 * ever asks.
 *
 * Only two things can put a value in here, and both come from the server:
 * `useRegimeProfile` (`GET /imports/regime-profile`), which is the sanctioned
 * currency source (uk-cbam-cost-breakdown D3/RD-2 — the client never names its
 * own regime), via the app-level mount in `AppLayout`.
 *
 * ── Why it is not persisted ────────────────────────────────────────────────
 *
 * A currency written to localStorage would outlive the org it describes: sign
 * in as a UK declarant, switch to an EU one, and the first paint of the new
 * org would print `£` on euros. It lives in memory and is cleared on org
 * switch (`OrgBoundary`, beside `queryClient.clear()`), so the worst case is
 * a re-fetch, never a wrong glyph.
 */

/** The currency a regime is denominated in when nothing has said otherwise. */
export const FALLBACK_CURRENCY = 'EUR';

/**
 * The only currencies a regime may be denominated in. A allow-list, not a
 * passthrough: this value ends up on every money figure in the app, so a
 * malformed or unexpected server response must degrade to the fallback rather
 * than reach `Intl.NumberFormat` (which throws on an invalid currency code and
 * would take a page down with it).
 */
const SUPPORTED = Object.freeze(['EUR', 'GBP']);

export const useRegimeCurrencyStore = create((set) => ({
  currency: null,

  /**
   * Record the currency the server reported for this declarant's regime.
   * Anything not on the allow-list clears the store rather than storing it,
   * so an unknown regime renders euros instead of throwing.
   */
  setRegimeCurrency: (currency) =>
    set({ currency: SUPPORTED.includes(currency) ? currency : null }),

  /** Forget it — on org switch and sign-out. */
  resetRegimeCurrency: () => set({ currency: null }),
}));

/**
 * The active regime's currency, for non-React callers.
 *
 * Never returns `null`: an unknown regime is euros, which is what every call
 * site got from `formatCurrency`'s literal default before this store existed.
 *
 * @returns {'EUR'|'GBP'}
 */
export const getRegimeCurrency = () =>
  useRegimeCurrencyStore.getState().currency ?? FALLBACK_CURRENCY;

/**
 * A NAMED regime's currency, rather than the signed-in one's.
 *
 * `getRegimeCurrency()` above answers "what currency is the viewer in?", which
 * is the right question everywhere except a surface showing two regimes at once:
 * the Analytics split view renders a UK half in sterling and an EU half in euros
 * on one page, for the same viewer (uk-cbam-analytics D18). That half knows its
 * regime, so it asks here. Same mapping, one definition — it was written out as
 * a ternary in two components before this existed.
 *
 * @param {'UK'|'EU'|null|undefined} regime
 * @returns {'EUR'|'GBP'} — an unknown regime is euros, as everywhere else.
 */
export const currencyForRegime = (regime) => (regime === 'UK' ? 'GBP' : FALLBACK_CURRENCY);

export const setRegimeCurrency = (currency) =>
  useRegimeCurrencyStore.getState().setRegimeCurrency(currency);

export const resetRegimeCurrency = () => useRegimeCurrencyStore.getState().resetRegimeCurrency();

export default useRegimeCurrencyStore;
